import { z } from 'zod';

// ============================================================================
// Enums
// ============================================================================

export const StockCategorySchema = z.enum([
  'WRISTBAND',
  'PRINTED_TICKET',
  'KIOSK_SUPPLY',
  'OFFICE_SUPPLY',
]);
export type StockCategory = z.infer<typeof StockCategorySchema>;

export const StockTransactionTypeSchema = z.enum([
  'RECEIPT',
  'ADJUSTMENT_UP',
  'ADJUSTMENT_DOWN',
  'CONSUMPTION',
  'TRANSFER_OUT',
  'TRANSFER_IN',
  'WRITE_OFF',
]);
export type StockTransactionType = z.infer<typeof StockTransactionTypeSchema>;

export const RebalanceDirectionSchema = z.enum([
  'ONLINE_TO_WALKUP',
  'WALKUP_TO_ONLINE',
]);
export type RebalanceDirection = z.infer<typeof RebalanceDirectionSchema>;

// ============================================================================
// Digital Inventory — Query Schemas
// ============================================================================

export const DigitalInventorySummaryQuerySchema = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  ticketTypeId: z.string().optional(),
});
export type DigitalInventorySummaryQuery = z.infer<typeof DigitalInventorySummaryQuerySchema>;

export const DigitalInventoryListQuerySchema = z.object({
  page: z.string().optional(),
  limit: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  ticketTypeId: z.string().optional(),
});
export type DigitalInventoryListQuery = z.infer<typeof DigitalInventoryListQuerySchema>;

// ============================================================================
// Digital Inventory — Request Schemas
// ============================================================================

export const UpdateAllocationSchema = z.object({
  totalCapacity: z.number().int().min(0).optional(),
  onlineAllocation: z.number().int().min(0).optional(),
  walkupAllocation: z.number().int().min(0).optional(),
  reason: z.string().min(1),
});
export type UpdateAllocationInput = z.infer<typeof UpdateAllocationSchema>;

export const RebalanceAllocationSchema = z.object({
  inventoryId: z.string().min(1),
  shiftDirection: RebalanceDirectionSchema,
  shiftQuantity: z.number().int().min(1),
  reason: z.string().min(1),
});
export type RebalanceAllocationInput = z.infer<typeof RebalanceAllocationSchema>;

export const BulkAllocationItemSchema = z.object({
  ticketTypeId: z.string().min(1),
  dateFrom: z.string().min(1),
  dateTo: z.string().min(1),
  totalCapacity: z.number().int().min(0),
  onlineAllocation: z.number().int().min(0),
  walkupAllocation: z.number().int().min(0),
});
export type BulkAllocationItem = z.infer<typeof BulkAllocationItemSchema>;

export const BulkAllocationSchema = z.object({
  allocations: z.array(BulkAllocationItemSchema).min(1),
  reason: z.string().min(1),
});
export type BulkAllocationInput = z.infer<typeof BulkAllocationSchema>;

// ============================================================================
// Physical Stock — Query Schemas
// ============================================================================

export const PhysicalStockQuerySchema = z.object({
  category: z.string().optional(),
  lowStockOnly: z.string().optional(),
  search: z.string().optional(),
  page: z.string().optional(),
  limit: z.string().optional(),
});
export type PhysicalStockQuery = z.infer<typeof PhysicalStockQuerySchema>;

export const PhysicalStockHistoryQuerySchema = z.object({
  page: z.string().optional(),
  limit: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  type: z.string().optional(),
});
export type PhysicalStockHistoryQuery = z.infer<typeof PhysicalStockHistoryQuerySchema>;

// ============================================================================
// Physical Stock — Request Schemas
// ============================================================================

export const CreatePhysicalStockItemSchema = z.object({
  code: z.string().min(1).max(50),
  name: z.string().min(1).max(100),
  category: StockCategorySchema,
  unit: z.string().min(1).max(20),
  currentQuantity: z.number().int().min(0).optional(),
  minimumThreshold: z.number().int().min(0).optional(),
  reorderPoint: z.number().int().min(0).optional(),
  reorderQuantity: z.number().int().min(0).optional(),
  locationCode: z.string().max(50).optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type CreatePhysicalStockItemInput = z.infer<typeof CreatePhysicalStockItemSchema>;

export const UpdatePhysicalStockItemSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  unit: z.string().min(1).max(20).optional(),
  minimumThreshold: z.number().int().min(0).optional(),
  reorderPoint: z.number().int().min(0).optional(),
  reorderQuantity: z.number().int().min(0).optional(),
  locationCode: z.string().max(50).optional(),
  isActive: z.boolean().optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type UpdatePhysicalStockItemInput = z.infer<typeof UpdatePhysicalStockItemSchema>;

export const RecordStockTransactionSchema = z.object({
  type: StockTransactionTypeSchema,
  quantity: z.number(),
  reason: z.string().optional(),
  referenceNumber: z.string().max(50).optional(),
  locationCode: z.string().max(50).optional(),
});
export type RecordStockTransactionInput = z.infer<typeof RecordStockTransactionSchema>;

// ============================================================================
// Response Schemas
// ============================================================================

export const DigitalInventoryRecordSchema = z.object({
  id: z.string(),
  inventoryDate: z.string(),
  ticketTypeId: z.string(),
  ticketTypeCode: z.string().nullable(),
  ticketTypeName: z.string().nullable(),
  totalCapacity: z.number().int(),
  onlineAllocation: z.number().int(),
  walkupAllocation: z.number().int(),
  soldOnline: z.number().int(),
  soldWalkup: z.number().int(),
  soldReseller: z.number().int(),
  reserved: z.number().int(),
  cancelled: z.number().int(),
  availableOnline: z.number().int(),
  availableWalkup: z.number().int(),
  utilizationPercent: z.number(),
  isBlocked: z.boolean(),
  blockReason: z.string().nullable(),
  priceMultiplier: z.number(),
  lastRebalancedAt: z.string().nullable(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type DigitalInventoryRecord = z.infer<typeof DigitalInventoryRecordSchema>;

export const DigitalInventorySummaryDaySchema = z.object({
  date: z.string(),
  ticketTypes: z.array(DigitalInventoryRecordSchema),
  totals: z.object({
    totalCapacity: z.number().int(),
    totalSold: z.number().int(),
    totalAvailable: z.number().int(),
    utilizationPercent: z.number(),
  }),
});
export type DigitalInventorySummaryDay = z.infer<typeof DigitalInventorySummaryDaySchema>;

export const RebalanceResultSchema = z.object({
  id: z.string(),
  previousOnlineAllocation: z.number().int(),
  previousWalkupAllocation: z.number().int(),
  newOnlineAllocation: z.number().int(),
  newWalkupAllocation: z.number().int(),
  lastRebalancedAt: z.string(),
});
export type RebalanceResult = z.infer<typeof RebalanceResultSchema>;

export const BulkAllocationResultSchema = z.object({
  totalProcessed: z.number().int(),
  details: z.array(z.object({
    date: z.string(),
    ticketTypeId: z.string(),
    action: z.enum(['created', 'updated']),
  })),
});
export type BulkAllocationResult = z.infer<typeof BulkAllocationResultSchema>;

export const PhysicalStockItemSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  category: z.string(),
  unit: z.string(),
  currentQuantity: z.number().int(),
  minimumThreshold: z.number().int(),
  reorderPoint: z.number().int(),
  reorderQuantity: z.number().int(),
  locationCode: z.string().nullable(),
  isActive: z.boolean(),
  isLowStock: z.boolean(),
  metadata: z.unknown().nullable(),
  lastTransactionAt: z.string().nullable(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});
export type PhysicalStockItem = z.infer<typeof PhysicalStockItemSchema>;

export const PhysicalStockItemDetailSchema = PhysicalStockItemSchema.extend({
  recentTransactions: z.array(z.object({
    id: z.string(),
    type: z.string(),
    quantity: z.number().int(),
    quantityBefore: z.number().int(),
    quantityAfter: z.number().int(),
    reason: z.string().nullable(),
    referenceNumber: z.string().nullable(),
    locationCode: z.string().nullable(),
    performedBy: z.string().nullable(),
    performedByName: z.string().nullable(),
    createdAt: z.string(),
  })),
});
export type PhysicalStockItemDetail = z.infer<typeof PhysicalStockItemDetailSchema>;

export const StockTransactionRecordSchema = z.object({
  id: z.string(),
  type: z.string(),
  quantity: z.number().int(),
  quantityBefore: z.number().int(),
  quantityAfter: z.number().int(),
  reason: z.string().nullable(),
  referenceNumber: z.string().nullable(),
  locationCode: z.string().nullable(),
  performedBy: z.string().nullable(),
  performedByName: z.string().nullable(),
  createdAt: z.string(),
});
export type StockTransactionRecord = z.infer<typeof StockTransactionRecordSchema>;

export const RecordStockTransactionResultSchema = z.object({
  transactionId: z.string(),
  stockItemId: z.string(),
  type: z.string(),
  quantity: z.number().int(),
  quantityBefore: z.number().int(),
  quantityAfter: z.number().int(),
  referenceNumber: z.string().nullable(),
  createdAt: z.string(),
});
export type RecordStockTransactionResult = z.infer<typeof RecordStockTransactionResultSchema>;
